import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Label } from '@/components/ui/shadcn/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/shadcn/radio-group';
import { useTranslatedLegacySettings } from '@/hooks/useTranslatedLegacySettings';
import { useTranslations } from '@/hooks/useTranslations';
import { logger } from '@/lib/utils';
import { router } from '@inertiajs/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface UnifiedPrioritySelectorProps {
    value: string;
    priorityType: 'company' | 'lead' | 'workOrder' | 'project';
    mode: 'editable' | 'form' | 'readonly';
    updateRoute?: string;
    resourceId?: number | string;
    onChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    showIcons?: boolean;
}

// Modal content component that can use the useModal hook
function PriorityModalContent({
    priorityType,
    selectedValue,
    setSelectedValue,
    showIcons = true,
}: {
    priorityType: 'company' | 'lead' | 'workOrder' | 'project';
    selectedValue: string;
    setSelectedValue: (value: string) => void;
    showIcons?: boolean;
}) {
    const { legacy } = useTranslatedLegacySettings();

    // Map priorityType to correct settings property
    const getPriorityOptions = (): Array<{ value: string; label: string; color: string; icon: string }> => {
        if (!legacy) return [];

        switch (priorityType) {
            case 'company':
                return legacy.COMPANY_PRIORITY_OPTIONS || [];
            case 'lead':
                return legacy.LEAD_PRIORITY_OPTIONS || [];
            case 'workOrder':
                return legacy.WORK_ORDER_PRIORITY_OPTIONS || [];
            case 'project':
                return legacy.COMPANY_PRIORITY_OPTIONS || [];
            default:
                return [];
        }
    };

    const priorityOptions = getPriorityOptions();
    logger.log('Priority Options:', priorityOptions); // Debug log

    const handleValueChange = (newValue: string) => {
        setSelectedValue(newValue);
    };

    const getPriorityOption = (priority: string) => {
        return priorityOptions.find((option: { value: string; label: string; color: string; icon: string }) => option.value === priority);
    };

    const getPriorityBadgeClasses = (priority: string) => {
        const option = getPriorityOption(priority);
        logger.log('Modal Priority:', priority, 'Option:', option); // Debug log
        if (!option) return 'bg-muted text-foreground border-border';
        return option.color;
    };

    const getPriorityIcon = (priority: string) => {
        const option = getPriorityOption(priority);
        if (!option || !option.icon) return null;

        // Use the actual icon from database - it could be a Unicode character or icon name
        return <span className="text-sm">{option.icon}</span>;
    };

    return (
        <div className="space-y-6">
            <RadioGroup value={selectedValue} onValueChange={handleValueChange} className="space-y-3">
                {priorityOptions.map((option: { value: string; label: string; color: string; icon: string }) => (
                    <div
                        key={option.value}
                        className="border-border hover:bg-accent flex items-center justify-between rounded-lg border p-3 transition-colors"
                    >
                        <Label htmlFor={option.value} className="flex flex-1 cursor-pointer items-center space-x-2">
                            {showIcons && getPriorityIcon(option.value)}
                            <span
                                className={`inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium ${getPriorityBadgeClasses(option.value)}`}
                            >
                                {option.label}
                            </span>
                        </Label>
                        <RadioGroupItem value={option.value} id={option.value} />
                    </div>
                ))}
            </RadioGroup>
        </div>
    );
}

// Modal footer component
function PriorityModalFooter({
    value,
    mode,
    updateRoute,
    resourceId,
    onChange,
    selectedValue,
    setSelectedValue,
    onClose,
}: Omit<UnifiedPrioritySelectorProps, 'placeholder' | 'className' | 'disabled' | 'priorityType'> & {
    selectedValue: string;
    setSelectedValue: (value: string) => void;
    onClose: () => void;
}) {
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslations();

    const handleSave = async () => {
        if (mode === 'form' && onChange) {
            onChange(selectedValue);
            onClose();
            return;
        }

        if (mode === 'editable' && updateRoute && resourceId) {
            setIsLoading(true);

            try {
                router.patch(
                    route(updateRoute, resourceId),
                    {
                        priority: selectedValue,
                    },
                    {
                        preserveState: true,
                        preserveScroll: true,
                        onSuccess: () => {
                            onClose();
                        },
                        onError: (errors) => {
                            console.error('Priority update failed:', errors);
                            toast.error(t('common.failed_to_update_priority', 'Failed to update priority'));
                            setSelectedValue(value); // Revert on error
                        },
                        onFinish: () => {
                            setIsLoading(false);
                        },
                    },
                );
            } catch (error) {
                console.error('Priority update failed:', error);
                toast.error(t('common.failed_to_update_priority', 'Failed to update priority'));
                setSelectedValue(value); // Revert on error
                setIsLoading(false);
            }
        }
    };

    const handleCancel = () => {
        setSelectedValue(value); // Revert to original value
        onClose();
    };

    return (
        <>
            <Button variant="outline" onClick={handleCancel} disabled={isLoading} className="w-full md:w-auto">
                {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleSave} disabled={isLoading || selectedValue === value} variant="default">
                {isLoading ? t('common.saving', 'Saving...') : t('common.save_priority', 'Save Priority')}
            </Button>
        </>
    );
}

// Main component
export function UnifiedPrioritySelector({
    value,
    priorityType,
    mode,
    updateRoute,
    resourceId,
    onChange,
    placeholder,
    className,
    disabled = false,
    showIcons = true,
}: UnifiedPrioritySelectorProps) {
    const { t } = useTranslations();
    const [selectedValue, setSelectedValue] = useState(value);
    const [isOpen, setIsOpen] = useState(false);
    const { legacy } = useTranslatedLegacySettings();
    const defaultPlaceholder = placeholder || t('common.select_priority', 'Select priority');

    // Sync selectedValue with value prop
    useEffect(() => {
        setSelectedValue(value);
    }, [value]);

    // Map priorityType to correct settings property
    const getPriorityOptions = (): Array<{ value: string; label: string; color: string; icon: string }> => {
        if (!legacy) return [];

        switch (priorityType) {
            case 'company':
                return legacy.COMPANY_PRIORITY_OPTIONS || [];
            case 'lead':
                return legacy.LEAD_PRIORITY_OPTIONS || [];
            case 'workOrder':
                return legacy.WORK_ORDER_PRIORITY_OPTIONS || [];
            case 'project':
                return legacy.COMPANY_PRIORITY_OPTIONS || [];
            default:
                return [];
        }
    };

    const priorityOptions = getPriorityOptions();
    const currentOption = priorityOptions.find((option: { value: string; label: string; color: string; icon: string }) => option.value === value);
    const displayValue = currentOption?.label || value || defaultPlaceholder;

    const getPriorityOption = (priority: string) => {
        return priorityOptions.find((option: { value: string; label: string; color: string; icon: string }) => option.value === priority);
    };

    const getPriorityBadgeClasses = (priority: string) => {
        const option = getPriorityOption(priority);
        if (!option) return 'bg-muted text-foreground border-border';
        return option.color;
    };

    const getPriorityIcon = (priority: string) => {
        const option = getPriorityOption(priority);
        if (!option || !option.icon) return null;

        // Use the actual icon from database - it could be a Unicode character or icon name
        return <span className="text-sm">{option.icon}</span>;
    };

    if (mode === 'readonly') {
        return (
            <span className={className}>
                <span
                    className={`inline-flex items-center justify-center space-x-1 rounded-md border px-2 py-0.5 text-xs font-medium ${getPriorityBadgeClasses(value)}`}
                >
                    {showIcons && getPriorityIcon(value)}
                    <span>{displayValue}</span>
                </span>
            </span>
        );
    }

    return (
        <ResponsiveModal open={isOpen} onOpenChange={setIsOpen} variant="dialog">
            <ResponsiveModalTrigger asChild>
                <Button
                    variant="ghost"
                    className={`h-auto p-1 ${className} ${disabled ? 'pointer-events-none cursor-not-allowed opacity-50' : ''}`}
                    disabled={disabled}
                >
                    <span
                        className={`inline-flex items-center justify-center space-x-1 rounded-md border px-2 py-0.5 text-xs font-medium ${getPriorityBadgeClasses(value)}`}
                    >
                        {showIcons && getPriorityIcon(value)}
                        <span>{displayValue}</span>
                    </span>
                </Button>
            </ResponsiveModalTrigger>
            <ResponsiveModalContent drawerClassName="data-[vaul-drawer-direction=bottom]:max-h-[85dvh]">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('common.select_priority', 'Select Priority')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>{t('common.choose_priority', 'Choose the priority level for this item')}</ResponsiveModalDescription>
                </ResponsiveModalHeader>
                <ResponsiveModalBody>
                    <PriorityModalContent
                        priorityType={priorityType}
                        selectedValue={selectedValue}
                        setSelectedValue={setSelectedValue}
                        showIcons={showIcons}
                    />
                </ResponsiveModalBody>
                <ResponsiveModalFooter>
                    <PriorityModalFooter
                        value={value}
                        mode={mode}
                        updateRoute={updateRoute}
                        resourceId={resourceId}
                        onChange={onChange}
                        selectedValue={selectedValue}
                        setSelectedValue={setSelectedValue}
                        onClose={() => setIsOpen(false)}
                    />
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
